import { getI18n } from "react-i18next";
import { profileRecord, profileOwn, validProfileDescriptor, type ProfileFeedbackDescriptor } from "../../../../src/lib/contracts/profileFeedbackPresentation";
import source from "./locales/en/profileFeedback";
import { sharedRemoteFeedback, sharedFeedbackText, type SharedRemoteFeedback } from "./sharedFeedbackPresentation";
import { mediaLibraryRemoteFeedback, mediaLibraryRemoteText, type MediaLibraryRemoteFeedback } from "./mediaLibraryFeedbackPresentation";
import { applicationRemoteFeedback, applicationFeedbackText, type ApplicationRemoteFeedback } from "./applicationFeedbackPresentation";
import { userRemoteFeedback, userFeedbackText, type UserRemoteFeedback } from "./userFeedbackPresentation";
import { notificationRemoteFeedback, notificationFeedbackText, type NotificationRemoteFeedback } from "./notificationFeedbackPresentation";
export type { ProfileFeedbackDescriptor } from "../../../../src/lib/contracts/profileFeedbackPresentation";
export type ProfileRemoteFeedback = { profileRemote: true; fallback: string } & (
  | { family: "profile"; value: ProfileFeedbackDescriptor }
  | { family: "shared"; value: SharedRemoteFeedback }
  | { family: "media"; value: MediaLibraryRemoteFeedback }
  | { family: "application"; value: ApplicationRemoteFeedback }
  | { family: "user"; value: UserRemoteFeedback }
  | { family: "notification"; value: NotificationRemoteFeedback }
);
export type ProfileMessage = string | ProfileRemoteFeedback;
/** Resolve only explicit producer identity, including the first aligned validation slot. */
export function profileRemoteFeedback(response: unknown): ProfileRemoteFeedback | undefined {
  if (!profileRecord(response)) return undefined;
  const nested = profileRecord(response.error) ? response.error : undefined;
  const fallback = typeof nested?.message === "string" ? nested.message : typeof response.message === "string" ? response.message : undefined;
  if (fallback === undefined) return undefined;
  const validation = Array.isArray(response.validationPresentation) ? response.validationPresentation : Array.isArray(nested?.validationPresentation) ? nested.validationPresentation : undefined;
  const descriptor = validation?.length ? validation[0] : nested?.messagePresentation ?? nested?.presentation ?? response.messagePresentation ?? response.presentation;
  const normalized = { message: fallback, messagePresentation: descriptor };
  const base = { profileRemote: true as const, fallback };
  if (validProfileDescriptor(descriptor, source)) return { ...base, family: "profile", value: { code: descriptor.code, params: { ...descriptor.params } } };
  const shared = sharedRemoteFeedback(normalized); if (shared) return { ...base, family: "shared", value: shared };
  const notification = notificationRemoteFeedback(normalized); if (notification) return { ...base, family: "notification", value: notification };
  const application = applicationRemoteFeedback(normalized); if (application) return { ...base, family: "application", value: application };
  const user = userRemoteFeedback(normalized); if (user) return { ...base, family: "user", value: user };
  const media = mediaLibraryRemoteFeedback(normalized); if (media) return { ...base, family: "media", value: media };
  return undefined;
}
export function profileResponseFeedback(response: unknown, fallback: string): ProfileMessage {
  const remote = profileRemoteFeedback(response); if (remote) return remote;
  if (profileRecord(response)) {
    const nested = profileRecord(response.error) ? response.error : undefined;
    if (typeof nested?.message === "string") return nested.message;
    if (typeof response.message === "string") return response.message;
  }
  return fallback;
}
export function profileFeedbackValue(fallback: string, descriptor: unknown): ProfileMessage {
  return profileRemoteFeedback({ message: fallback, messagePresentation: descriptor }) ?? fallback;
}
export function profileFeedbackText(value: ProfileMessage): string {
  if (typeof value === "string") return value;
  switch (value.family) {
    case "shared": return sharedFeedbackText(value.value);
    case "media": return mediaLibraryRemoteText(value.value);
    case "application": return applicationFeedbackText(value.value);
    case "user": return userFeedbackText(value.value);
    case "notification": return notificationFeedbackText(value.value);
    case "profile": {
      if (!validProfileDescriptor(value.value, source)) return value.fallback;
      const engine = getI18n(); const locale = engine?.resolvedLanguage ?? engine?.language ?? "en";
      const catalog: Record<string, unknown> | undefined = engine?.isInitialized ? engine.getResourceBundle(locale, "profileFeedback") : undefined;
      const code = value.value.code as keyof typeof source;
      const text = catalog && profileOwn(catalog, code) && typeof catalog[code] === "string" ? catalog[code] : source[code];
      return text.replace(/{{\s*(\w+)\s*}}/g, (match, name: string) => profileOwn(value.value.params, name)
        ? typeof value.value.params[name] === "number" ? new Intl.NumberFormat(locale).format(value.value.params[name]) : String(value.value.params[name]) : match);
    }
  }
}
export class ProfileFeedbackError extends Error {
  constructor(readonly feedback: ProfileMessage) { super(typeof feedback === "string" ? feedback : feedback.fallback); }
}
